import { Typography, Tooltip, Dropdown, Tag } from "antd";
import CustomButton from "../../../components/CustomButton/Button";
import { formatCurrency} from "../../../helpers/helpers";
import { DEAL_STATUS_LABEL_MAP } from "../../../constants/constants";
import { safeStringSort, safeNumberSort, safeDateSort } from "../../../helpers/sortHelpers";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import { MoreOutlined } from "@ant-design/icons";
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";

const { Text } = Typography;

// 🔹 Updated backend status codes → friendly keys
const STATUS_MAP = {
  DSC1000001: "pending",
  DSC1000002: "approved",
  DSC1000003: "expired",
  DSC1000005: "funded",
  DSC1000006: "disbursed",
  DSC1000017: "pendingDisbursement",
  DSC1000007: "cancelled",
  DSC1000008: "repayment",
  DSC1000009: "partiallySettled", // repaid
  DSC1000010: "settled",
  DSC1000011: "closed",
  DSC1000012: "rejected" };

export const getDealsColumns = ({
  status,
  dealCategoryId = null,
  sendAgreeloading,
  loadingDeals,
  onAssign,
  onReject,
  onCancel,
  onRepublish,
  onSendTOAgree,
  onDisburse,
  onSendEmail,
  onRepaymentReminder,
  onOverdueReminder,
  onSendTds,
  onView,
  onEditTenure,
  currentUser,
  privileges }) => {
  const statusArray = Array.isArray(status) ? status : (status ? [status] : []);
  const mappedStatuses = statusArray.map((s) => STATUS_MAP[s] || s);
  const isOnlyPending = mappedStatuses.length === 1 && mappedStatuses[0] === "pending";
  const isOnlyDisbursed = mappedStatuses.length === 1 && mappedStatuses[0] === "disbursed";
  const includesPending = mappedStatuses.includes("pending");
  const includesApproved = mappedStatuses.includes("approved");

  const isSupplyChain = isSupplyChainCategory(dealCategoryId);

  const hasActions =
    statusArray.length === 0 ||
    !statusArray.every((s) => {
      const mapped = STATUS_MAP[s] || s;
      return mapped === "rejected" || mapped === "closed";
    });

  const columns = [
    {
      title: "Deal ID",
      dataIndex: "dealId",
      key: "dealId",
      width: 120,
      sorter: (a, b) => safeStringSort(a?.dealId, b?.dealId),
      fixed: "left" },
    {
      title: "Enterprise Name",
      dataIndex: "entName",
      key: "entName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.entName, b?.entName) },
    {
      title: "Business Name",
      dataIndex: "orgName",
      key: "orgName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.orgName, b?.orgName) },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      sorter: (a, b) => {
        const statusA = DEAL_STATUS_LABEL_MAP?.[a?.statusId] || a?.statusName || a?.dealStatus || "";
        const statusB = DEAL_STATUS_LABEL_MAP?.[b?.statusId] || b?.statusName || b?.dealStatus || "";
        return safeStringSort(statusA, statusB);
      },
      render: (_, record) => {
        const statusText =
          DEAL_STATUS_LABEL_MAP?.[record?.statusId] ||
          record?.statusName ||
          record?.dealStatus ||
          record?.statusId ||
          "N/A";
        return <Tag color="blue">{statusText}</Tag>;
      } },

    // Conditionally show Invoice Amount / Purchase Order Amount (not for disbursed only)
    ...(!isOnlyDisbursed
      ? [
        {
          title: isSupplyChain ? "Purchase Order Amount" : "Invoice Amount",
          dataIndex: "totalInvoiceAmt",
          key: "totalInvoiceAmt",
          width: 160,
          sorter: (a, b) => safeNumberSort(a?.totalInvoiceAmt, b?.totalInvoiceAmt),
          render: (val) => formatCurrency(val || 0) },
      ]
      : []),
    {
      title: "Tenure",
      dataIndex: "tenure",
      key: "tenure",
      width: 120,
      sorter: (a, b) => safeNumberSort(a?.tenure, b?.tenure) },

    // ✅ Disbursed → Disbursed Amount + Expected Repayment Date
    ...(isOnlyDisbursed
      ? [
        {
          title: "Disbursed Amount",
          dataIndex: "disbursedAmt",
          key: "disbursedAmt",
          width: 180,
          sorter: (a, b) => safeNumberSort(a?.disbursedAmt, b?.disbursedAmt),
          render: (val) => formatCurrency(val || 0) },
        {
          title: "Expected Repayment Date",
          dataIndex: "expectedRepaymentDate",
          key: "expectedRepaymentDate",
          width: 200,
          sorter: (a, b) => safeDateSort(a?.expectedRepaymentDate, b?.expectedRepaymentDate),
          render: (val) => (val ? new Date(val).toLocaleDateString() : "N/A") },
      ]
      : isOnlyPending
        ? [
          {
            title: "Fund Required",
            dataIndex: "fundRequired",
            key: "fundRequired",
            width: 200,
            sorter: (a, b) => safeNumberSort(a?.fundRequired, b?.fundRequired),
            render: (val) => formatCurrency(val || 0) },
        ]
        : includesPending && includesApproved
          ? [
            {
              title: "Fund Required",
              dataIndex: "fundRequired",
              key: "fundRequired",
              width: 160,
              sorter: (a, b) => safeNumberSort(a?.fundRequired, b?.fundRequired),
              render: (val) => formatCurrency(val || 0) },
            {
              title: "Approved Amount",
              dataIndex: "approvedFundAmt",
              key: "approvedFundAmt",
              width: 160,
              sorter: (a, b) => safeNumberSort(a?.approvedFundAmt, b?.approvedFundAmt),
              render: (val) => formatCurrency(val || 0) },
          ]
          : [
            {
              title: "Approved Amount",
              dataIndex: "approvedFundAmt",
              key: "approvedFundAmt",
              width: 180,
              sorter: (a, b) => safeNumberSort(a?.approvedFundAmt, b?.approvedFundAmt),
              render: (val) => formatCurrency(val || 0) },
          ]),

    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 180,
      sorter: (a, b) => safeStringSort(a?.remarks, b?.remarks),
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 160, display: "block" }}>
              {text}
            </Text>
          </Tooltip>
        ) : (
          "N/A"
        ) },
    ...(hasActions
      ? [
        {
          title: "Actions",
          key: "actions",
          fixed: "right",
          render: (_, record) => {
            const rowStatusCode =
              record?.statusId ||
              (Array.isArray(status)
                ? status.length === 1
                  ? status[0]
                  : null
                : status);
            const rowDealStatus = rowStatusCode
              ? STATUS_MAP[rowStatusCode] || rowStatusCode
              : (statusArray.length > 0
                ? STATUS_MAP[statusArray[0]] || statusArray[0]
                : "pending");

            const expectedDate = record?.expectedRepaymentDate
              ? new Date(record.expectedRepaymentDate)
              : null;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const isOverdue = expectedDate ? expectedDate < today : false;

            const isDisbursedRepaid =
              rowDealStatus === "disbursed" || rowDealStatus === "partiallySettled";

            const items = [];

            // View
            if (
              rowDealStatus !== "pending" &&
              rowDealStatus !== "rejected" &&
              rowDealStatus !== "closed"
            ) {
              items.push({
                key: "view",
                label: "View" });
            }

            // Pending
            if (rowDealStatus === "pending") {
              if (privileges.includes(PERM_CODE.DPSAP)) {
                items.push({
                  key: "assign",
                  label: "Assign" });
              }

              if (privileges.includes(PERM_CODE.DPSRE)) {
                items.push({
                  key: "reject",
                  label: <span style={{ color: "#ff4d4f" }}>Reject</span> });
              }
            }

            // Approved
            if (rowDealStatus === "approved") {
              if (privileges.includes(PERM_CODE.DASCA)) {
                items.push({
                  key: "cancel",
                  label: <span style={{ color: "#ff4d4f" }}>Cancel</span> });
              }
            }

            // Expired
            if (rowDealStatus === "expired") {
              if (privileges.includes(PERM_CODE.DESCA)) {
                items.push({
                  key: "cancel",
                  label: <span style={{ color: "#ff4d4f" }}>Cancel</span> });
              }

              if (privileges.includes(PERM_CODE.DESRE)) {
                items.push({
                  key: "republish",
                  label: "Republish",
                  disabled:
                    Number(record?.approvedFundAmt) ===
                    Number(record?.fundedAmt) });
              }

              if (privileges.includes(PERM_CODE.DESAG)) {
                items.push({
                  key: "disburseAgreement",
                  label: "Disburse" });
              }
            }

            // Funded
            if (rowDealStatus === "funded") {
              if (privileges.includes(PERM_CODE.DGSDI)) {
                items.push({
                  key: "disburse",
                  label: "Disburse" });
              }

              if (privileges.includes(PERM_CODE.DGEML)) {
                items.push({
                  key: "sendMail",
                  label: loadingDeals?.[record.dealId]
                    ? "Sending..."
                    : "Send Mail" });
              }

              if (privileges.includes(PERM_CODE.DGSCA)) {
                items.push({
                  key: "cancel",
                  label: <span style={{ color: "#ff4d4f" }}>Cancel</span> });
              }
            }

            // Disbursed
            if (
              rowDealStatus === "disbursed" &&
              privileges.includes(PERM_CODE.DDEML)
            ) {
              items.push({
                key: isOverdue ? "overdueReminder" : "repaymentReminder",
                label: isOverdue
                  ? "Send Overdue Reminder"
                  : "Send Repayment Reminder" });
            }

            // Partially Settled
            if (
              rowDealStatus === "partiallySettled" &&
              privileges.includes(PERM_CODE.DREML)
            ) {
              items.push({
                key: isOverdue ? "overdueReminder" : "repaymentReminder",
                label: isOverdue
                  ? "Send Overdue Reminder"
                  : "Send Repayment Reminder" });
            }

            return (
              <>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items,
                    onClick: ({ key }) => {
                      switch (key) {
                        case "view":
                          onView?.(record);
                          break;

                        case "assign":
                          onAssign?.(record);
                          break;

                        case "reject":
                          onReject?.(record);
                          break;

                        case "editTenure":
                          onEditTenure?.(record);
                          break;
                        case "cancel":
                          onCancel?.(record);
                          break;

                        case "republish":
                          onRepublish?.(record);
                          break;

                        case "disburseAgreement":
                          onSendTOAgree?.(record);
                          break;

                        case "disburse":
                          onDisburse?.(record);
                          break;

                        case "sendMail":
                          onSendEmail?.(record);
                          break;

                        case "repaymentReminder":
                          onRepaymentReminder?.(record);
                          break;

                        case "overdueReminder":
                          onOverdueReminder?.(record);
                          break;

                        default:
                          break;
                      }
                    } }}
                >
                  <CustomButton
                    type="text"
                    icon={<MoreOutlined style={{ fontSize: 20 }} />}
                  />
                </Dropdown>
                {isDisbursedRepaid && isOverdue && (
                  <Tag color="error">Overdue</Tag>
                )}
              </>
            );
          } },
      ]
      : []),
  ];

  return columns;
};
